import React, { useContext, useState } from 'react'
import {
  FlatList,
  Image,
  ImageSourcePropType,
  Pressable,
  StyleSheet,
  Text,
  View,
} from 'react-native'
import {
  BG_STYLES,
  COMMON_STYLES,
  DefaultProductImage,
  LazyImage,
  RightArrowIcon,
  Spinner,
  TEXT_STYLES,
  TYPOGRAPHY,
  VARIANTS_STYLES,
} from '@dehaat/dds'
import { rnNavigation } from 'app/utils/appConfig'
import RequestHeader from 'app/components/priceRequest/RequestHeader'
import { ROUTES } from 'app/routes/types'
import useFetchVendors from 'app/hooks/useFetchVendors'
import useDebounce from 'app/hooks/useDebounce'
import { ResponsiveContext } from 'app/context/ResponsiveUIContext'
import { setVendorId } from 'app/store/reducer/vendor'
import { useDispatch, useSelector } from 'react-redux'
import { selectRoleType } from 'app/store/reducer/requestflow'

const NewRequest = () => {
  const [searchQuery, setSearchQuery] = useState('')
  const [applyFilter, setApplyFilter] = useState({})
  const debouncedSearchQuery = useDebounce(searchQuery)
  const { mediaStyles } = useContext(ResponsiveContext)
  const roleType = useSelector(selectRoleType)
  const { fetchNext, request, recentVendors, loading, loadingNext } =
    useFetchVendors({
      category: (applyFilter as any)?.dc_category,
      node_id: (applyFilter as any)?.node,
      state_id: (applyFilter as any)?.state,
      search_query: debouncedSearchQuery,
      role_type: roleType,
    })

  const dispatch = useDispatch()
  const renderCenter = (item: any) => (
    <Pressable
      onPress={() => {
        dispatch(setVendorId(item.id))
        rnNavigation({ name: ROUTES.CHANGE_PRICE })
      }}
      style={styles.card}
    >
      <View style={styles.container}>
        <View style={[styles.wrapper, { flex: 1 }]}>
          <LazyImage
            imageUrl={item.image_url}
            fallBackIcon={
              <Image
                source={DefaultProductImage as ImageSourcePropType}
                style={styles.productImage}
              />
            }
            imgStyles={styles.productImage}
          />
          <View style={[COMMON_STYLES['ml3/4'], { flex: 1, minWidth: 0 }]}>
            <Text style={[styles.name]} numberOfLines={1} ellipsizeMode="tail">
              {item.company_name}
            </Text>
            <Text style={styles.address}>
              {[item.district?.name, item.state?.name]
                .filter(Boolean)
                .join(', ')}
            </Text>
          </View>
        </View>
        <RightArrowIcon strokeColor="black" width={20} />
      </View>
      <View
        style={{ height: 1, backgroundColor: '#E0E0E0', marginVertical: 12 }}
      />
      <View
        style={mediaStyles({
          desktopStyle: {
            flexDirection: 'row',
            alignItems: 'center',
            marginTop: 8,
          },
          mobileStyle: {
            marginTop: 8,
          },
        })}
      >
        <View style={styles.infoRow}>
          <Text
            style={[
              styles.labelText,
              mediaStyles({
                mobileStyle: { width: '40%' },
                desktopStyle: {
                  ...TYPOGRAPHY.CAPTION1,
                },
              }),
            ]}
          >
            Vendor Id:{' '}
          </Text>
          <Text style={[styles.valueText,mediaStyles({
                desktopStyle: {
                  ...TYPOGRAPHY.CAPTION1,
                },
              })]}>{item.id}</Text>
        </View>
        <View style={styles.infoRow}>
          <Text
            style={[
              styles.labelText,
              mediaStyles({
                mobileStyle: { width: '40%' },
                desktopStyle: {
                  ...TYPOGRAPHY.CAPTION1,
                },
              }),
            ]}
          >
            {' '}
            Partner Id:{' '}
          </Text>
          <Text style={[styles.valueText,mediaStyles({
                desktopStyle: {
                  ...TYPOGRAPHY.CAPTION1,
                },
              })]}>{item.partner_id || '--'}</Text>
        </View>
        <View style={styles.infoRow}>
          <Text
            style={[
              styles.labelText,
              mediaStyles({
                mobileStyle: { width: '40%' },
                desktopStyle: {
                  ...TYPOGRAPHY.CAPTION1,
                },
              }),
            ]}
          >
            {' '}
            Node:{' '}
          </Text>
          <Text style={[styles.valueText,mediaStyles({
                desktopStyle: {
                  ...TYPOGRAPHY.CAPTION1,
                },
              })]}>{item.node?.name || '--'}</Text>
        </View>
      </View>
    </Pressable>
  )

  if (loading) return <Spinner />

  return (
    <>
      <RequestHeader
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        setAppliedFilters={setApplyFilter}
        appliedFilters={applyFilter}
      />

      <FlatList
        data={request}
        keyExtractor={item => item.id.toString()}
        renderItem={({ item }) => renderCenter(item)}
        onEndReached={fetchNext}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={[
          COMMON_STYLES.pb2,
          mediaStyles({
            desktopStyle: {
              maxWidth: '70%',
              width: '100%',
              alignSelf: 'center',
            },
          }),
        ]}
        ListHeaderComponent={
          <View style={{ marginTop: 16 }}>
            {recentVendors?.length > 0 && (
              <View style={{ marginBottom: 16 }}>
                <Text style={styles.heading}>Recent</Text>
                {recentVendors.map(vendor => (
                  <View key={vendor.id}>{renderCenter(vendor)}</View>
                ))}
              </View>
            )}
            <Text style={styles.heading}>Stores</Text>
          </View>
        }
        ListEmptyComponent={
          <View
            style={[
              COMMON_STYLES['ph3/4'],
              COMMON_STYLES.pt1,
              COMMON_STYLES.flex1,
              COMMON_STYLES.alignItemsCenter,
              COMMON_STYLES.justifyCenter,
            ]}
          >
            <Text
              style={[
                TYPOGRAPHY.TEXT2,
                VARIANTS_STYLES.BASE,
                TEXT_STYLES.textNeutral80,
              ]}
            >
              The list is currently empty
            </Text>
          </View>
        }
      />
      {loadingNext ? <Spinner type="in-screen" /> : null}
    </>
  )
}

const styles = StyleSheet.create({
  card: {
    ...COMMON_STYLES.p1,
    ...BG_STYLES.bgWhite,
    borderRadius: 12,
    marginVertical: 6,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 4,
    elevation: 2,
    marginHorizontal: 8,
  },
  container: {
    ...COMMON_STYLES.row,
    ...COMMON_STYLES.justifyBetween,
    ...COMMON_STYLES.alignItemsCenter,
  },
  wrapper: {
    ...COMMON_STYLES.row,
    ...COMMON_STYLES.alignItemsCenter,
  },
  heading: {
    ...TEXT_STYLES.textNeutral90,
    ...TYPOGRAPHY.CAPTION1,
    ...VARIANTS_STYLES.MEDIUM,
    ...COMMON_STYLES['mh1/2'],
  },
  type: {
    ...TEXT_STYLES.textNeutral70,
    ...TYPOGRAPHY.CAPTION2,
    ...VARIANTS_STYLES.SEMI_EMPHASIZED,
  },
  name: {
    ...TEXT_STYLES.textNeutral90,
    ...TYPOGRAPHY.TEXT1,
    ...VARIANTS_STYLES.SEMI_EMPHASIZED,
  },
  address: {
    ...TEXT_STYLES.textNeutral70,
    ...TYPOGRAPHY.CAPTION1,
    ...VARIANTS_STYLES.MEDIUM,
  },
  labelText: {
    ...TEXT_STYLES.textNeutral70,
    ...TYPOGRAPHY.CAPTION2,
    ...VARIANTS_STYLES.BASE,
  },
  valueText: {
    ...TEXT_STYLES.textNeutral90,
    ...TYPOGRAPHY.CAPTION2,
    ...VARIANTS_STYLES.BASE,
  },
  infoRow: {
    ...COMMON_STYLES.row,
    marginBottom: 4,
    marginRight: 16,
    flex: 1,
  },
  productImage: {
    width: 32,
    height: 32,
  },
})

export default NewRequest
